import { Input, Select, Button, Switch, Tooltip, Tag } from "antd";
import { useMemo, useState, useEffect } from "react";
import { useGetClients } from "~/services/client";
import { TClient } from "~/types";
import { InventoryStatusCode, ClientStatus, RateType } from "~/utils/constants";
import { calculateCubicVolume } from "~/utils";
import { AlertsSection } from "./alert-section";
import { ClientInventoryTable } from "./client-inventory-table";
import { ClientTable } from "./client-table";
import { LocationBreakdown } from "./location-breakdown";
import { SummaryCards } from "./summary-card";
import { SortConfig } from "./type";
import {
  useGetInventoryMetrics,
  useExportInventory,
} from "~/services/inventory";
import { useNotification } from "~/hooks";
import { Download, Receipt } from "lucide-react";
import { StorageInvoiceModal } from "./storage-invoice";
import { StorageBillingHistory } from "./storage-billing-history";
import { BatchInvoiceModal } from "./batch-invoice-modal";
import { StorageInvoice } from "~/services/storage/storage.api";

const { Search } = Input;
const { Option } = Select;

export const AHCStorage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [selectedClient, setSelectedClient] = useState<TClient | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("All");
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: "companyName",
    direction: "ascend",
  });
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  const [isBatchModalOpen, setIsBatchModalOpen] = useState(false);
  const [showStorageClientsOnly, setShowStorageClientsOnly] = useState(true);
  const [viewingInvoice, setViewingInvoice] = useState<StorageInvoice | null>(null);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(1); // Reset to first page when searching
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const {
    data: clients,
    isLoading,
    isRefetching,
  } = useGetClients({
    params: {
      inventoryStatusCode: InventoryStatusCode.AVAILABLE_ON_HAND,
      rateType: showStorageClientsOnly ? RateType.STORAGE_CLIENT : undefined,
      page,
      pageSize,
      search: debouncedSearchTerm || undefined,
      status:
        statusFilter !== "All" ? (statusFilter as ClientStatus) : undefined,
      sortByName:
        sortConfig.key === "companyName" || sortConfig.key === "lastName"
          ? sortConfig.direction === "ascend"
            ? "ASC"
            : "DESC"
          : undefined,
    },
  });

  // Export functionality
  const exportAllMutation = useExportInventory();
  const { success, error } = useNotification();

  const handleExportAllInventory = () => {
    exportAllMutation.mutate(
      { status: InventoryStatusCode.AVAILABLE_ON_HAND },
      {
        onSuccess: () => {
          success({
            message: "Export successful",
            description: "All inventory data has been exported to CSV",
          });
        },
        onError: (err) => {
          error({
            message: "Export failed",
            description:
              err.message || "There was an error exporting the inventory data",
          });
        },
      }
    );
  };

  const totalClients: number = clients?.meta.itemCount || 0;
  const totalPackages = useMemo(() => {
  return clients?.items.reduce((sum, client) => {
    // Filter inventory to only count items with "available on hand" status
    const availableOnHandPackages = client.inventoryClient?.filter(
      (inventory) => inventory.status === InventoryStatusCode.AVAILABLE_ON_HAND
    ).length || 0;
    return sum + availableOnHandPackages;
  }, 0);
}, [clients?.items]);

  // Calculate total estimated monthly revenue across all clients
  const totalMonthly = useMemo(() => {
    if (!clients?.items) return 0;

    return clients.items.reduce((total, client) => {
      // Group inventory by location to apply minimum per location
      const byLocation = client.inventoryClient
        ?.filter((inv) => !inv.parentInventoryId) // Only count parent items
        .reduce((acc, item) => {
          const locationId = item.location?.id || 'no-location';
          if (!acc[locationId]) {
            acc[locationId] = {
              items: [],
              location: item.location,
            };
          }
          acc[locationId].items.push(item);
          return acc;
        }, {} as Record<string, { items: any[], location?: any }>) || {};

      // Calculate cost for this client applying minimum per location
      const clientTotal = Object.values(byLocation).reduce((clientSum, { items, location }) => {
        const totalCubicFeet = items.reduce((sum, item) => {
          const volume = calculateCubicVolume(item) / 1728;
          return sum + volume;
        }, 0);

        const minimumCubicFeet = Number(location?.minimumCubicFeet) || 0;
        const billableCubicFeet = Math.max(totalCubicFeet, minimumCubicFeet);
        const ratePerCubicFt = Number(location?.ratePerCubicFt) || 0;

        return clientSum + (billableCubicFeet * ratePerCubicFt);
      }, 0);

      return total + clientTotal;
    }, 0);
  }, [clients?.items]);

  const { data } = useGetInventoryMetrics();

  const handlePageChange = (newPage: number, newPageSize: number) => {
    setPage(newPage);
    setPageSize(newPageSize);
    // Clear selected client when pagination changes
    setSelectedClient(null);
  };

  const handleStatusChange = (value: string) => {
    setStatusFilter(value);
    setPage(1); // Reset to first page when filtering
    setSelectedClient(null);
  };

  const requestSort = (key: keyof TClient) => {
    let direction: "ascend" | "descend" = "ascend";
    if (sortConfig.key === key && sortConfig.direction === "ascend") {
      direction = "descend";
    }
    setSortConfig({ key, direction });
    setPage(1); // Reset to first page when sorting
    setSelectedClient(null);
  };

  // Remove client-side filtering and sorting since we're using server-side
  const displayClients = clients?.items || [];

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h1 style={{ fontSize: 20, fontWeight: "bold", margin: 0 }}>
          Client Inventory & Storage
        </h1>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Tooltip title={showStorageClientsOnly
            ? "Currently showing only clients marked as 'Storage Client'. Toggle off to see all clients with inventory."
            : "Showing all clients with inventory. Toggle on to show only 'Storage Client' rate type."
          }>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Switch
                checked={showStorageClientsOnly}
                onChange={(checked) => {
                  setShowStorageClientsOnly(checked);
                  setPage(1);
                  setSelectedClient(null);
                }}
                size="small"
              />
              <span style={{ fontSize: 13, color: showStorageClientsOnly ? "#1890ff" : "#666" }}>
                {showStorageClientsOnly ? "Storage Clients Only" : "All Clients w/ Inventory"}
              </span>
            </div>
          </Tooltip>
          {!showStorageClientsOnly && (
            <Tag color="orange" style={{ marginLeft: 8 }}>
              Review Mode
            </Tag>
          )}
        </div>
      </div>

      <SummaryCards
        totalClients={totalClients}
        totalPackages={totalPackages ?? 0}
        totalCubicVolume={data?.totalStorageSpace.toFixed(1) ?? "0"}
        totalMonthly={totalMonthly}
      />

      <LocationBreakdown
        totalVolumeBK={data?.totalVolumeBK.toFixed(1) ?? "0"}
        totalVolumeLI={data?.totalVolumeLI.toFixed(1) ?? "0"}
      />

      <AlertsSection />

      <div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 16,
          }}
        >
          <h2 style={{ fontSize: 14, fontWeight: 500 }}>Client Inventory</h2>
          <div style={{ display: "flex", gap: 8 }}>
            <Button
              type="primary"
              icon={<Receipt size={16} />}
              onClick={() => setIsInvoiceModalOpen(true)}
              style={{ display: "flex", alignItems: "center" }}
            >
              Create Invoice
            </Button>
            <Button
              icon={<Receipt size={16} />}
              onClick={() => setIsBatchModalOpen(true)}
              style={{ display: "flex", alignItems: "center" }}
            >
              Batch Create
            </Button>
            <Button
              icon={<Download size={16} />}
              onClick={handleExportAllInventory}
              loading={exportAllMutation.isPending}
              style={{ display: "flex", alignItems: "center" }}
            >
              Export All
            </Button>
            <Search
              placeholder="Search..."
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSearchTerm(e.target.value)
              }
              style={{ width: 200 }}
            />
            <Select
              value={statusFilter}
              onChange={handleStatusChange}
              style={{ width: 120 }}
            >
              <Option value="All">All</Option>
              <Option value={ClientStatus.ACTIVE}>Active</Option>
              <Option value={ClientStatus.INACTIVE}>Inactive</Option>
            </Select>
          </div>
        </div>

        <ClientTable
          sortedClients={displayClients}
          sortConfig={sortConfig}
          requestSort={requestSort}
          setSelectedClient={setSelectedClient}
          selectedClient={selectedClient}
          clientsData={clients}
          onPageChange={handlePageChange}
          isLoading={isLoading || isRefetching}
        />

        {selectedClient && (
          <ClientInventoryTable
            client={selectedClient}
            clientInventory={selectedClient.inventoryClient}
          />
        )}
      </div>

      {/* Billing History */}
      <StorageBillingHistory
        onViewInvoice={(invoice) => {
          // Find the client from our loaded data to pass to the modal
          const client = clients?.items.find(c => c.id === invoice.clientId);
          if (client) {
            setSelectedClient(client);
          }
          setViewingInvoice(invoice);
          setIsInvoiceModalOpen(true);
        }}
      />

      {/* Storage Invoice Modal */}
      <StorageInvoiceModal
        visible={isInvoiceModalOpen}
        onClose={() => {
          setIsInvoiceModalOpen(false);
          setViewingInvoice(null);
        }}
        client={selectedClient}
        allClients={clients?.items || []}
        existingInvoice={viewingInvoice}
      />

      {/* Batch Invoice Modal */}
      <BatchInvoiceModal
        visible={isBatchModalOpen}
        onClose={() => setIsBatchModalOpen(false)}
        allClients={clients?.items || []}
      />
    </>
  );
};
